/**
 * The result view model (UX-SPEC §4.7): everything the main thread needs to show an analysis, built in the worker
 * from `CoreAnalysis` + `EnsembleResult` so the main thread never holds a `CoreAnalysis`. Structured-cloneable; the
 * typed arrays are transferred. Types only.
 */
import type {
	ChannelId,
	ChannelSegment,
	DocEstimate,
	Family,
	UnitEstimate,
} from '../engine/channels/types.ts';
import type { EnsembleResult } from '../engine/ensemble/index.ts';
import type { SegmentIndex } from '../engine/ensemble/segments-index.ts';
import type { IntakeFormat } from '../engine/ingest/types.ts';
import type { ConcreteFormat } from './protocol.ts';

/** Bins of the hotspot-density strip and the maximum points of any downsampled line (UX-PERF-08). */
export const STRIP_BINS = 2000;
export const MAX_POINTS = 2000;

export type SourceKind = 'file' | 'paste' | 'sample' | 'url';

export interface ResultMeta {
	name: string;
	sourceKind: SourceKind;
	format: IntakeFormat;
	dispatchedFormat?: ConcreteFormat;
	bytes: number;
	/** The intake word count (the number the 500,000-word cap is checked against). */
	words: number;
	proseWords: number;
	paragraphs: number;
	chapters: number;
	scenes: number;
	language: { code: string; confidence: number };
	parseMs: number;
	pages?: number;
	warnings: string[];
	finalUrl?: string;
	/** ISO 8601. */
	analysedAt: string;
	/** `doc.hash` as 8 hex digits. */
	docHash: string;
	engineVersion: string;
	model: { id: string; calibrated: boolean; lexiconVersion: string };
	/**
	 * The analysis is of version `n` of the document whose version 0 has docHash `root` (DESIGN UD-27). Absent for an upload.
	 */
	version?: { root: string; n: number };
}

/** An ensemble result without the per-segment aggregate internals (not shown anywhere; they are most of its size). */
export type SlimSegment = Omit<SegmentIndex, 'aggregate'>;
export type SlimEnsemble = Omit<EnsembleResult, 'segments'> & {
	segments: { joint: SlimSegment[]; units: SlimSegment[] };
};

/** Paragraphs in reading order; every array has `count` entries. */
export interface ParaTable {
	count: number;
	charStart: Uint32Array;
	charEnd: Uint32Array;
	words: Uint32Array;
	/** Index into `chapters` (−1 when none). */
	chapter: Int32Array;
	scene: Int32Array;
	/** Indices into `blocks` of the paragraph's blocks: [blockFirst[p], blockFirst[p+1]). */
	blockFirst: Uint32Array;
}

export const BLOCK_PARAGRAPH = 0;
export const BLOCK_HEADING = 1;
export const BLOCK_LIST = 2;
export const BLOCK_CODE = 3;
export const BLOCK_TABLE = 4;
export const BLOCK_SCENE_BREAK = 5;
export const BLOCK_OTHER = 6;

/** Blocks in reading order (a paragraph's attached headings and scene breaks come before its own block). */
export interface BlockTable {
	count: number;
	/** BLOCK_* */
	kind: Uint8Array;
	/** Heading level 1–6, else 0. */
	level: Uint8Array;
	charStart: Uint32Array;
	charEnd: Uint32Array;
	para: Uint32Array;
}

export interface HeadingEntry {
	para: number;
	level: number;
	text: string;
	charStart: number;
}

export interface UnitInfo {
	kind: 'chapter' | 'scene';
	paraStart: number;
	paraEnd: number;
	charStart: number;
	charEnd: number;
	words: number;
	title?: string;
}

/**
 * Every evidence span of every channel, sorted by `start` (ties by channel), no cap (C-UI-EVMAP, G18).
 * `channel` indexes `CHANNEL_IDS`; `feature` indexes that channel's `features`.
 */
export interface SpanTable {
	count: number;
	start: Uint32Array;
	end: Uint32Array;
	channel: Uint8Array;
	feature: Uint8Array;
	weight: Float32Array;
	/** Spans that start in paragraph p: [paraFirst[p], paraFirst[p+1]) (length paragraphs + 1). */
	paraFirst: Uint32Array;
	/** CSR of the spans that start before paragraph p but reach into it: coverIdx[coverStart[p]..coverStart[p+1]). */
	coverStart: Uint32Array;
	coverIdx: Uint32Array;
}

export interface Profile {
	/** Paragraph index (fractional after downsampling). */
	x: Float32Array;
	/** The smoothed per-paragraph rate in the channel's ramp units (ramp units = K/E × unitScale). */
	y: Float32Array;
}

export interface ChannelDetail {
	id: ChannelId;
	name: string;
	family: Family;
	axis: 'authorship' | 'translation';
	/** +1: a higher value is more machine-like; −1: a lower value is. */
	direction: 1 | -1;
	unitLabel: string;
	unitScale: number;
	exposureUnit: string;
	estimator: string;
	/** Feature labels, indexed by a span's `feature`. */
	features: string[];
	/** The human-typical value in ramp units (where the ramp gives 20). */
	priorMu: number;
	status: 'scored' | 'abstain';
	reason?: string;
	estimate?: DocEstimate;
	segments: ChannelSegment[];
	spikes: ChannelSegment[];
	units: UnitEstimate[];
	notes: string[];
	flagsWithheld?: string;
	/** The channel's named secondary features (e.g. PROC-01 R6). */
	namedFeatures: Record<string, number>;
	/** The engine's summary lines, top items first. */
	summary: string[];
	spanCount: number;
	/** Span count per feature index. */
	featureSpanCounts: number[];
	/** Event-type channels only (the others have no per-paragraph series). */
	profile: Profile | null;
}

export interface StripData {
	bins: number;
	/** Hotspot entries per bin: row c (CHANNEL_IDS index) then one final row for all channels; withheld channels are zero. */
	density: Uint16Array;
	rows: number;
}

export interface ViewModel {
	version: 1;
	meta: ResultMeta;
	ensemble: SlimEnsemble;
	text: string;
	paras: ParaTable;
	blocks: BlockTable;
	headings: HeadingEntry[];
	chapters: UnitInfo[];
	scenes: UnitInfo[];
	/** Flat pairs [start, end, …] of the scene-break lines in `text`. */
	sceneBreaks: Uint32Array;
	/** In CHANNEL_IDS order. */
	channels: ChannelDetail[];
	spans: SpanTable;
	strip: StripData;
}
